import React, { Dispatch, SetStateAction, useMemo } from "react";
import styles from "./checkbox.module.scss";
import {
  CheckboxChecked as CheckboxCheckedSmall,
  CheckboxDefault as CheckboxDefaultSmall,
  CheckboxDisabled as CheckboxDisabledSmall,
} from "../../icons/18";
import {
  CheckboxChecked as CheckboxCheckedMedium,
  CheckboxDefault as CheckboxDefaultMedium,
  CheckboxDisabled as CheckboxDisabledMedium,
} from "../../icons/24";
import {
  SwitchBasicChecked as SwitchCheckedMedium,
  SwitchBasicDefault as SwitchDefaultMedium,
  SwitchBasicDisabled as SwitchDisabledMedium,
  SwitchBasicOval as SwitchOvalMedium,
  SwitchTableChecked as SwitchTableCheckedSmall,
  SwitchTableDefault as SwitchTableDefaultSmall,
  SwitchTableDisabled as SwitchTableDisabledSmall,
  SwitchTableOval as SwitchTableOvalSmall,
} from "../../icons/misc";

interface CheckBoxProps {
  id: string;
  size?: "small" | "medium" | "large";
  name: string;
  value: string;
  setValue: Dispatch<SetStateAction<string>> | ((val: string) => void);
  checked: boolean;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  labelComponent?: React.ReactNode;
  appearance?: "default" | "switch";
  className?: string;
}

const Checkbox = ({
  id,
  className,
  size = "medium",
  name,
  value,
  setValue,
  checked,
  onClick = () => {},
  disabled = false,
  labelComponent,
  appearance = "default",
}: CheckBoxProps) => {
  // 이전 스타일 미적용될 수 있어서 추가
  const prevClassCheck = className
    ? className?.includes("input-checkbox-wrap") || className?.includes("input-switch-wrap")
    : false;

  const CheckboxIcon = React.memo(
    ({
      size,
      type,
      disabled,
      checked,
      attributes,
    }: {
      size: "small" | "medium" | "large";
      type: "default" | "switch";
      disabled: boolean;
      checked: boolean;
      attributes?: { [key: string]: any };
    }) => {
      const iconStatus = disabled ? "Disabled" : checked ? "Checked" : "Default";
      if (type === "default") {
        if (size === "small") {
          if (iconStatus === "Checked") {
            return <CheckboxCheckedSmall {...attributes} />;
          } else if (iconStatus === "Default") {
            return <CheckboxDefaultSmall {...attributes} />;
          } else if (iconStatus === "Disabled") {
            return <CheckboxDisabledSmall {...attributes} />;
          }
        }
        if (size === "medium") {
          if (iconStatus === "Checked") {
            return <CheckboxCheckedMedium {...attributes} />;
          } else if (iconStatus === "Default") {
            return <CheckboxDefaultMedium {...attributes} />;
          } else if (iconStatus === "Disabled") {
            return <CheckboxDisabledMedium {...attributes} />;
          }
        }
      } else if (type === "switch") {
        if (size === "small") {
          if (iconStatus === "Checked") {
            return <SwitchTableCheckedSmall {...attributes} />;
          } else if (iconStatus === "Default") {
            return <SwitchTableDefaultSmall {...attributes} />;
          } else if (iconStatus === "Disabled") {
            return <SwitchTableDisabledSmall {...attributes} />;
          }
        }
      }
      if (size === "medium") {
        if (iconStatus === "Checked") {
          return <SwitchCheckedMedium {...attributes} />;
        } else if (iconStatus === "Default") {
          return <SwitchDefaultMedium {...attributes} />;
        } else if (iconStatus === "Disabled") {
          return <SwitchDisabledMedium {...attributes} />;
        }
      }

      return null;
    },
  );

  const IconMemo = useMemo(() => {
    return (
      <>
        <CheckboxIcon
          size={size}
          type={appearance}
          disabled={disabled}
          checked={checked}
          attributes={{
            viewBox:
              appearance === "switch"
                ? size === "small"
                  ? "0 0 42 30"
                  : "0 0 56 40"
                : size === "small"
                  ? "0 0 18 18"
                  : "0 0 24 24",
            preserveAspectRatio: "xMidYMid meet",
            width: appearance === "default" ? "100%" : "",
          }}
        />
        {appearance === "switch" && (
          <span>
            {size === "medium" && (
              <SwitchOvalMedium viewBox={"0 0 28 28"} preserveAspectRatio={"xMidYMid meet"} />
            )}
            {size === "small" && (
              <SwitchTableOvalSmall viewBox={"0 0 21 22"} preserveAspectRatio={"xMidYMid meet"} />
            )}
          </span>
        )}
      </>
    );
  }, [size, appearance, disabled, checked]);

  return (
    <>
      <div
        className={`${appearance === "switch" ? styles["input-switch-wrap"] : styles["input-checkbox-wrap"]} ${styles[size]} ${className ? className : ""}`}
      >
        <input
          id={id}
          type="checkbox"
          name={name}
          className={`${appearance === "switch" ? styles["input-switch"] : styles["input-checkbox"]} ${prevClassCheck ? (appearance === "switch" ? "input-swtich" : "input-checkbox") : ""} only-sr`}
          value={value}
          checked={checked}
          onChange={() => setValue(value)}
          onClick={(e) => onClick(e)}
          disabled={disabled}
        />
        <label
          // htmlFor={id} // input 영역으로 스크롤 이동되는 현상 방지하기 위해 htmlFor 대신 onClick 사용
          onClick={(e) => {
            e.preventDefault();
            setValue(value);
          }}
        >
          {IconMemo}
        </label>
      </div>
      {labelComponent && (
        <label
          className={`${appearance === "switch" ? styles["input-switch-label"] : styles["input-checkbox-label"]} 
          ${prevClassCheck ? (appearance === "switch" ? "input-switch-label" : "input-checkbox-label") : ""}`}
          // htmlFor={id} // input 영역으로 스크롤 이동되는 현상 방지하기 위해 htmlFor 대신 onClick 사용
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const target = e?.target as HTMLElement;
            if (target?.tagName === "A") {
              window.location.href = target.getAttribute("href") || "";
            } else {
              setValue(value);
            }
          }}
        >
          {labelComponent}
        </label>
      )}
    </>
  );
};

export default Checkbox;
